import { Observable, ReplaySubject, from, interval, merge, of, zip } from "rxjs";
import { catchError, every, filter, first, map, mergeMap, switchMap, take, tap } from "rxjs/operators";
import _ from 'lodash'

import { AssociatedMediasResource, Expression, Item, MEDIA_SUBJECTS_KEYS, Manifestation, Media, MediaType, PushCachedValueResult, ReplaySubjectContent, SynchroMediaUpdates, Wemi, Work, EntityType } from "@skinsoft/types";
import { SyncUpDependency } from "@skinsoft/classes-interfaces-definition";
import { NoIdInWemiError, NoItemInWemiError } from "./errors";

import { ExpressionOfflineDataUpdateCacher, ItemOfflineDataUpdateCacher, ManifestationOfflineDataUpdateCacher, OfflineDataFetcher, OfflineDataUpdateCacher, WorkOfflineDataUpdateCacher } from "@skinsoft/offline";
import { AbstractSyncUpHandler } from "../../abstract";
import { dbCacheUp } from "@skinsoft/offline/dexie/cache-up";
import { WemiUtils } from "@skinsoft/wemi-utils";
import { ImageTransferHelper, MediaTransferHelper } from "@skinsoft/media-transfer-helper";
import { ItemDataStore } from "@skinsoft/entity";
import { extractCachedWemiChanges } from "./utils";
import { SynchroUpIdMapping } from "@skinsoft/synchro-up-mapping";

declare const config
const NB_TRY_FETCH_MEDIA_SYNC_UP: number = config.syncUp.medias.maxNbRetry
const INTERVAL_FETCH_MEDIA_SYNC_UP: number = config.syncUp.medias.delayBetweenRetry

export class WemiSyncUpHandler extends AbstractSyncUpHandler<Item, Wemi> {
    private itemOfflineDataUpdateCacher: OfflineDataUpdateCacher<Item>
    private manifestationOfflineDataUpdateCacher: OfflineDataUpdateCacher<Manifestation>
    private expressionOfflineDataUpdateCacher: OfflineDataUpdateCacher<Expression>
    private workOfflineDataUpdateCacher: OfflineDataUpdateCacher<Work>

    private itemDataStore: ItemDataStore

    private imageCache = dbCacheUp.media
    private synchronizationMap: {[key: string]: string} = {}
    private synchroUpIdMapping: SynchroUpIdMapping

    public static getInstance() {
        if (!WemiSyncUpHandler.instance) {
            WemiSyncUpHandler.instance = new WemiSyncUpHandler()
        }

        return WemiSyncUpHandler.instance
    }

    public static install(Vue) {
        WemiSyncUpHandler.getInstance()
    }

    private static instance: WemiSyncUpHandler = null as any

    private constructor() {
        super()
        this.itemOfflineDataUpdateCacher = new ItemOfflineDataUpdateCacher()
        this.expressionOfflineDataUpdateCacher = new ExpressionOfflineDataUpdateCacher()
        this.manifestationOfflineDataUpdateCacher = new ManifestationOfflineDataUpdateCacher()
        this.workOfflineDataUpdateCacher = new WorkOfflineDataUpdateCacher()

        this.itemDataStore = ItemDataStore.getInstance()
        this.synchroUpIdMapping = SynchroUpIdMapping.getInstance()
    }

    getType(): EntityType {
        return EntityType.WEMI
    }

    getCachedObjectsIds(): Observable<string[]> {
        return from(
            this.itemOfflineDataUpdateCacher.getCachedObjectsKeys(true, false)
        )
    }

    getDependenciesIds(id: string): Observable<SyncUpDependency[]> {
        return this.getCachedEntity(id).pipe(
            map(item => {
                return this.getCachedAssociatedMediasIds(item)
            })
        )
    }

    getCachedAssociatedMediasIds(entity: Item): SyncUpDependency[] {
        const res: SyncUpDependency[] = []

        if(entity.associatedMedias) {

            if(entity.associatedMedias?.first && MediaTransferHelper.isLocalMedia(entity.associatedMedias.first)) {
                res.push({
                    id: entity.associatedMedias.first,
                    type: EntityType.MEDIA,
                })
            }

            if(entity.associatedMedias?.second && MediaTransferHelper.isLocalMedia(entity.associatedMedias.second)) {
                res.push({
                    id: entity.associatedMedias.first,
                    type: EntityType.MEDIA,
                })
            }

            if(entity.associatedMedias?.third && MediaTransferHelper.isLocalMedia(entity.associatedMedias.third)) {
                res.push({
                    id: entity.associatedMedias.first, 
                    type: EntityType.MEDIA,
                })
            }

            if(entity.associatedMedias?.medias?.length) {
                for(const mediaId of entity.associatedMedias.medias) {
                    if(MediaTransferHelper.isLocalMedia(mediaId)) {
                        res.push({
                            id: mediaId,
                            type: EntityType.MEDIA,
                        })
                    }
                }
            }
        }

        return _.uniqBy(res, 'id')
    }
    
    getCachedEntity(key: string): Observable<Item> {
        return from(
            this.itemOfflineDataUpdateCacher.fetchOneFromId(key)
        ) as Observable<Item>
    }

    updateCachedAssociatedMediasIds(entity: Item): Observable<Item> {
        if((entity as any).associatedMedias) {
            return of(null).pipe(
                switchMap(async () => {
                    const entityNoType: any = entity
                    const updatedEntity = _.cloneDeep(entity)
        
                    if(entityNoType.associatedMedias?.first && MediaTransferHelper.isLocalMedia(entityNoType.associatedMedias.first)) {
                        const newId = await this.synchroUpIdMapping.getNewId(entityNoType.associatedMedias.first)
                        if(!newId) {
                            throw new Error()
                        }
        
                        _.set(
                            updatedEntity as unknown as object,
                            'associatedMedias.first',
                            newId
                        )
                    }
        
                    if(entityNoType.associatedMedias?.second && MediaTransferHelper.isLocalMedia(entityNoType.associatedMedias.second)) {
                        const newId = await this.synchroUpIdMapping.getNewId(entityNoType.associatedMedias.second)
                        if(!newId) {
                            throw new Error()
                        }
                        
                        _.set(
                            updatedEntity as unknown as object,
                            'associatedMedias.second',
                            newId
                        )
                    }
        
                    if(entityNoType.associatedMedias?.third && MediaTransferHelper.isLocalMedia(entityNoType.associatedMedias.third)) {
                        const newId = await this.synchroUpIdMapping.getNewId(entityNoType.associatedMedias.third)
                        if(!newId) {
                            throw new Error()
                        }
                        
                        _.set(
                            updatedEntity as unknown as object,
                            'associatedMedias.third',
                            newId
                        )
                    }
        
                    if(entityNoType.associatedMedias?.medias?.length) {
                        const newMedias = await Promise.all(
                            entityNoType.associatedMedias.medias.map(async mediaId => {
                                if(MediaTransferHelper.isLocalMedia(mediaId)) {
                                    const newId = await this.synchroUpIdMapping.getNewId(mediaId)
                                    if(!newId) {
                                        throw new Error()
                                    }
                                    return newId
                                }
            
                                return mediaId
                            })
                        )
        
                        _.set(
                            updatedEntity as unknown as object,
                            'associatedMedias.medias',
                            newMedias
                        )
                    }
        
                    return updatedEntity
                })
            )

        }

        return of(entity)
    }

    getNbCachedObjects(countLocallyCreated?: boolean | undefined): Promise<number> {
        return this.itemOfflineDataUpdateCacher.getNbCachedObjects()
    }
    getNbLocallyCreatedEntities(): Promise<number> {
        return this.itemOfflineDataUpdateCacher.getNbCachedCreations()
    }

    getAllCachedEntities(includeCashedCreations: boolean): Observable<Item[]> {
        return from(
            this.itemOfflineDataUpdateCacher.getAllRealObjects(includeCashedCreations) as Promise<Item[]>
        )
    }
    formatDataBeforePush(item: Item): Observable<Wemi> {
        return this.updateCachedAssociatedMediasIds(item).pipe(
            switchMap(itemWithMediasIds => {
                return zip(
                    of(itemWithMediasIds),
                    WemiUtils.getWemiFromItemOffline(item.id)
                )
            }),
            switchMap(([formattedItem, cachedWemi]) => {
                const result = _.cloneDeep(cachedWemi)
                result.item = formattedItem
                return of(result)
            }),
        )
    }

    getLocalAssociatedMedias(entity: Wemi): AssociatedMediasResource {
        if(entity.item) {
            const localAssociatedMedias: AssociatedMediasResource = {}
            const itemAssociatedMedias = entity.item.associatedMedias
    
            if(itemAssociatedMedias?.medias?.length) {
                const localMedias = itemAssociatedMedias.medias.filter(mediaId => {
                    return MediaTransferHelper.isLocalMedia(mediaId)
                })
    
                localAssociatedMedias.medias = localMedias ?? undefined // assign undefined in case localMedias == null
    
            }
            if(itemAssociatedMedias?.first) {
                if(MediaTransferHelper.isLocalMedia(itemAssociatedMedias.first)) {
                    localAssociatedMedias.first = itemAssociatedMedias.first
                }
            }
            if(itemAssociatedMedias?.second) {
                if(MediaTransferHelper.isLocalMedia(itemAssociatedMedias.second)) {
                    localAssociatedMedias.second = itemAssociatedMedias.second
                }
            }
            if(itemAssociatedMedias?.third) {
                if(MediaTransferHelper.isLocalMedia(itemAssociatedMedias.third)) {
                    localAssociatedMedias.third = itemAssociatedMedias.third
                }
            }
    
            return localAssociatedMedias
        }

        throw new NoItemInWemiError()
    }
    getLocalMediasKeys(entity: Wemi): string[] | null {
        if(entity.item) {
            if(entity.item.associatedMedias) {
                const associatedMedias = entity.item.associatedMedias
                let localMediasKeys: string[] = []
    
                if(associatedMedias.first) {
                    if(MediaTransferHelper.isLocalMedia(associatedMedias.first)) {
                        localMediasKeys.push(associatedMedias.first)
                    }
                }
                if(associatedMedias.second) {
                    if(MediaTransferHelper.isLocalMedia(associatedMedias.second)) {
                        localMediasKeys.push(associatedMedias.second)
                    }
                }
                if(associatedMedias.third) {
                    if(MediaTransferHelper.isLocalMedia(associatedMedias.third)) {
                        localMediasKeys.push(associatedMedias.third)
                    }
                }
    
                if(associatedMedias.medias?.length) {
                    localMediasKeys = localMediasKeys.concat(
                        associatedMedias.medias.filter(mediaId => {
                            return MediaTransferHelper.isLocalMedia(mediaId)
                        })
                    )
                }
    
                return _.uniq(localMediasKeys)
            }
            return null
        }

        throw new NoItemInWemiError()
    }
    removeLocalMedias(entity: Wemi): Wemi {
        const cleanedWemi = {...entity}
        
        if(entity.item) {
            const cleanedItem: Item = {...entity.item}

            if(entity.item?.associatedMedias) {
                const associatedMedias = entity.item!.associatedMedias
    
                if(associatedMedias?.first && MediaTransferHelper.isLocalMedia(associatedMedias?.first)) {
                    _.set(cleanedItem, 'associatedMedias.first', undefined)
                }
                if(associatedMedias?.second && MediaTransferHelper.isLocalMedia(associatedMedias?.second)) {
                    _.set(cleanedItem, 'associatedMedias.second', undefined)
                }
                if(associatedMedias?.third && MediaTransferHelper.isLocalMedia(associatedMedias?.third)) {
                    _.set(cleanedItem, 'associatedMedias.third', undefined)
                }
    
                if(associatedMedias.medias?.length) {
                    cleanedItem.associatedMedias.medias = (cleanedItem as any).associatedMedias.medias
                        .filter(mediaId => {
                            return !MediaTransferHelper.isLocalMedia(mediaId)
                        })
                }
            }
    
            cleanedWemi.item = cleanedItem
            return cleanedWemi
        }
        
        throw new NoItemInWemiError()
    }

    buildUploadCachedMediasReplaySubject(localMediasKeys: string[] | null): { [replayName: string]: ReplaySubject<ReplaySubjectContent>; } {
        const mediaSubjects: { [replayName: string]: ReplaySubject<ReplaySubjectContent> } = {}
        
        if (localMediasKeys?.length) {
            mediaSubjects[MEDIA_SUBJECTS_KEYS.image] = new ReplaySubject()
            const mediaTransferHelper = MediaTransferHelper.getInstance()
            
            const localImages = localMediasKeys.filter(mediaKey => mediaTransferHelper.getMediaType(mediaKey) === MediaType.IMAGE)
            
            const imageTransferHelper = ImageTransferHelper.getInstance()
            const mediaObservable = merge(from(localImages)).pipe(
                mergeMap((localImageKey) => {
                    return from(this.imageCache.get(localImageKey)).pipe(
                        map(localImageValue => localImageValue as unknown as (string | File)),
                        map(localImageValue => {
                            if(localImageValue instanceof File) {
                                return localImageValue
                            } else {
                                return mediaTransferHelper.convertBase64ToFile(localImageValue, 'image/jpg')
                            }
                        }),
                        map(blob => imageTransferHelper.uploadImage('image/jpg', new File([blob], `temp.${blob.type.split('/').pop()}`), 'data:')),
                        map((uploadResult): ReplaySubjectContent => {
                            return {
                                cacheKeys: [uploadResult.cacheKey, localImageKey]
                            }
                        }),
                        catchError(err => of({
                            error: err
                        } as ReplaySubjectContent)),
                    )
                }),
                mergeMap(uploadRes => {
                    if(!uploadRes.error && uploadRes.fileName) {
                        return from(
                            imageTransferHelper.repeatFindingMediaIdFromFileNameWithLimit(
                                uploadRes.fileName,
                                NB_TRY_FETCH_MEDIA_SYNC_UP,
                                INTERVAL_FETCH_MEDIA_SYNC_UP
                            )
                        ).pipe(
                            map(media => {
                                if(media.id) {
                                    const result = _.cloneDeep(uploadRes)
                                    result.newMediaId = media.id
                                    return result
                                }

                                throw new Error("Failed to find media")
                            }),
                        )
                    }
                    
                    return of(uploadRes)
                }),
                filter(downloadRes => !!downloadRes.newMediaId),
                take(localImages.length),
            )

            mediaObservable.subscribe(mediaSubjects[MEDIA_SUBJECTS_KEYS.image])
        }

        return mediaSubjects
    }

    getEntityId(entity: Wemi): string {
        if(entity.item?.id) {
            return entity.item.id
        }
        throw new NoIdInWemiError()
    }
    isCachedCreation(id: string): boolean {
        return OfflineDataUpdateCacher.isTemporaryId(id) as boolean
    }

    applyBeforePushOperations(dataToPush: SynchroMediaUpdates<Wemi>): Observable<[PushCachedValueResult, SynchroMediaUpdates<Wemi>]> {
        return of([
            { success: true } as PushCachedValueResult,
            dataToPush
        ])
    }

    private getAndFormatModificationsBeforeMerge(localEntity: Wemi): Observable<Partial<Wemi>> {
        return of(extractCachedWemiChanges(localEntity))
    }

    private mergeModificationsInOriginalEntity(original: Wemi, modification: Partial<Wemi>): Observable<Wemi> {
        const mergedWemi = WemiUtils.mergeUpdatedWemiWithOriginalWemi(original, modification)
        const originalIdentifier: string | undefined = _.get(
            original, 
            'item.identificationItemSchema.identifiers[0].identifier'
        )
        _.set(
            mergedWemi,
            'item.identificationItemSchema.identifiers[0].identifier',
            originalIdentifier
        )
        return of(mergedWemi)
    }

    pushCachedCreationOrUpdate(dataToPush: SynchroMediaUpdates<Wemi>, onlineWemi: Wemi): Observable<PushCachedValueResult> {
        return of(onlineWemi).pipe(
            switchMap(originalEntity => {
                return zip(
                    of(originalEntity),
                    this.getAndFormatModificationsBeforeMerge(dataToPush.body)
                )
            }),
            switchMap(([originalEntity, modifications]) => {
                return this.mergeModificationsInOriginalEntity(originalEntity, modifications)
            }),
            switchMap(mergedWemi => {
                return WemiUtils.saveWemi(mergedWemi, true)
            }),
            switchMap(updateResponse => {
                return WemiUtils.getWemiFromItem(this.getEntityId(onlineWemi))
            }),
            first(),
            catchError(e => {
                return of(Error('Error during update...'))
            }),
            switchMap(response => {
                if (!(response instanceof Error)) {
                    return of({ success: true })
                }
                return of({ success: false })
            })
        )
    }

    pushCachedUpdate(dataToUpdate: SynchroMediaUpdates<Wemi>): Observable<PushCachedValueResult> {
        return WemiUtils.getWemiFromItemOnLine(dataToUpdate.id).pipe(
            switchMap(onLineWemi => {
                return this.pushCachedCreationOrUpdate(dataToUpdate, onLineWemi) 
            })
        )
    }

    pushCachedCreations(dataToCreate: SynchroMediaUpdates<Wemi>): Observable<PushCachedValueResult> {
        return this.itemDataStore.create().pipe(
            switchMap(item => {
                return WemiUtils.getWemiFromItemOnLine(
                    item.id,
                )
            }),
            switchMap(createdWemi => {
                this.saveMappingAllOldIdsNewIds(dataToCreate.body, createdWemi)
                return this.pushCachedCreationOrUpdate(dataToCreate, createdWemi).pipe(
                    map(pushResult => {
                        return {
                            ...pushResult,
                            newId: createdWemi.item?.id
                        }
                    })
                )
            }),
        )
    }

    saveMappingOldIdNewId(oldId: string, newId: string): void {
        this.synchronizationMap[oldId] = newId
    }

    saveMappingAllOldIdsNewIds(oldWemi: Wemi, newWemi: Wemi): void {
        if(oldWemi.item?.id && newWemi.item?.id) {
            this.synchronizationMap[oldWemi.item.id] = newWemi.item.id
        }

        if(oldWemi.manifestation?.id && newWemi.manifestation?.id) {
            this.synchronizationMap[oldWemi.manifestation.id] = newWemi.manifestation.id
        }

        if(oldWemi.expression?.id && newWemi.expression?.id) {
            this.synchronizationMap[oldWemi.expression.id] = newWemi.expression.id
        }

        if(oldWemi.work?.id && newWemi.work?.id) {
            this.synchronizationMap[oldWemi.work.id] = newWemi.work.id
        }
    }

    getAfterPushAdditionalData(dataToPush: SynchroMediaUpdates<Wemi>): Observable<any> {
        return of(undefined as any)
    }

    applyAfterPushOperations(pushResult: PushCachedValueResult, dataPushed: SynchroMediaUpdates<Wemi>, additionalData?: any): Observable<PushCachedValueResult> {
        return of(pushResult)//this.handleReplaySubjects(pushResult, dataPushed)
    }

    handleReplaySubjects(pushResult: PushCachedValueResult, dataToPush: SynchroMediaUpdates<Wemi>, additionalData?: any): Observable<PushCachedValueResult> {
        let itemId = dataToPush.id
        if(OfflineDataFetcher.isTemporaryId(dataToPush.id)) {
            if(pushResult.newId) {
                itemId = pushResult.newId
            }
            else {
                const itemIdFromSyncMap = this.synchronizationMap[dataToPush.id]
                if(itemIdFromSyncMap && itemIdFromSyncMap !== "inProgress") {
                    itemId = itemIdFromSyncMap
                }
            }
        }
        
        if(itemId) {
            const imageSubject = dataToPush.mediaSubjects[MEDIA_SUBJECTS_KEYS.image]

            if(imageSubject) {
                let newFirstMediaId: string | null = null

                const timer = interval(1000)

                // combination zip + timer = time output from another source
                return zip(imageSubject, timer).pipe(
                    map(([replaySubjectContent, timer]) => replaySubjectContent),
                    mergeMap((replaySubjectContent): Observable<PushCachedValueResult> => {
                        if(replaySubjectContent.newMediaId) {
                            return this.itemDataStore.addImage2(itemId, replaySubjectContent as { newMediaId: string }).pipe(
                                tap(() => {
                                    ImageTransferHelper.getInstance().deleteImageInCache(replaySubjectContent.newMediaId as string)
                                    
                                    if(replaySubjectContent.cacheKeys) {
                                        for(const cacheKey of replaySubjectContent.cacheKeys) {
                                            if(dataToPush.cachedMedias?.first && cacheKey === dataToPush.cachedMedias.first) {
                                                newFirstMediaId = replaySubjectContent.newMediaId as string
                                            }

                                            ImageTransferHelper.getInstance().deleteImageInCache(cacheKey)
                                        }
                                    }
                                }),
                                tap(() => {
                                    ImageTransferHelper.getInstance().addImageToReporterSubAlbum(replaySubjectContent.newMediaId)
                                }),
                                switchMap(() => of({
                                    success: true
                                })),
                            )
                        }

                        return of({ success: false })
                    }),
                    every((result: PushCachedValueResult) => result.success),
                    switchMap(allSucceded => {
                        if(newFirstMediaId) {
                            this.itemDataStore.updateFirstMedia(itemId, newFirstMediaId).subscribe()
                        }

                        return of({ success: allSucceded })
                    })
                )
            } else {
                return of(pushResult)
            }
        }

        return of({ success: false })        
    }

    getCacheKeysToClean(pushResult: PushCachedValueResult, dataPushed: SynchroMediaUpdates<Wemi>): string[] {
        const keysToClean: string[] = []
        
        const cacheItemId = dataPushed.body.item?.id
        const cacheManifestationId = dataPushed.body.manifestation?.id
        const cacheExpressionId = dataPushed.body.expression?.id
        const cacheWorkId = dataPushed.body.work?.id

        if(cacheItemId) {
            keysToClean.push(cacheItemId)
            if(this.synchronizationMap[cacheItemId]) {
                keysToClean.push(this.synchronizationMap[cacheItemId])
            }
        }

        if(cacheManifestationId) {
            keysToClean.push(cacheManifestationId)
            if(this.synchronizationMap[cacheManifestationId]) {
                keysToClean.push(this.synchronizationMap[cacheManifestationId])
            }
        }

        if(cacheExpressionId) {
            keysToClean.push(cacheExpressionId)
            if(this.synchronizationMap[cacheExpressionId]) {
                keysToClean.push(this.synchronizationMap[cacheExpressionId])
            }
        }

        if(cacheWorkId) {
            keysToClean.push(cacheWorkId)
            if(this.synchronizationMap[cacheWorkId]) {
                keysToClean.push(this.synchronizationMap[cacheWorkId])
            }
        }

        return keysToClean
    }

    cleanCacheAfterPush(cacheKeys: string[]): void {
        for(const key of cacheKeys) {
            this.itemOfflineDataUpdateCacher.removeCachedValue(key)
            this.manifestationOfflineDataUpdateCacher.removeCachedValue(key)
            this.expressionOfflineDataUpdateCacher.removeCachedValue(key)
            this.workOfflineDataUpdateCacher.removeCachedValue(key)
        }
    }

    public getNewId(oldId: string): string | undefined {
        return this.synchronizationMap[oldId]
    }
}